/**
 * Active project drill-through context.
 *
 * When a user clicks "View All Findings" / "View All Resources" / "View in Graph"
 * from the Projects dashboard, this context is stored in sessionStorage and
 * read by destination pages so they can:
 *   1. Show a banner "Showing X for project: NAME [× clear]"
 *   2. Pre-populate filters (tag filters, project_id query params)
 *
 * sessionStorage (not localStorage) so the context dies with the tab —
 * matches Wiz behavior where project scope is per-tab navigation.
 */

const KEY = 'active_project_context';

export interface ActiveProjectContext {
  id: string;
  name: string;
  tags: Record<string, string>;
}

export function setActiveProjectContext(ctx: ActiveProjectContext | null): void {
  try {
    if (ctx === null) {
      sessionStorage.removeItem(KEY);
    } else {
      sessionStorage.setItem(KEY, JSON.stringify(ctx));
    }
  } catch {
    /* sessionStorage may be disabled (private mode) — silently ignore */
  }
}

export function getActiveProjectContext(): ActiveProjectContext | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.id || !parsed.name) {
      return null;
    }
    return {
      id: String(parsed.id),
      name: String(parsed.name),
      tags: (parsed.tags && typeof parsed.tags === 'object') ? parsed.tags : {},
    };
  } catch {
    return null;
  }
}

export function clearActiveProjectContext(): void {
  setActiveProjectContext(null);
}
